import React from "react";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";

interface ListFilters {
  page: number;
  limit: number;
  recipientType: "CONSUMER" | "TECHNICIAN" | "BUSINESS";
  filterType?: "STATIC" | "DYNAMIC";
  isActive?: boolean[];
  search_data?: string;
  listOwnership: "my-lists" | "all-lists";
}

interface ListFilterPanelProps {
  currentFilters: Partial<ListFilters>;
  onApplyFilters: (filters: Partial<ListFilters>) => void;
  onResetFilters: () => void;
}

export const ListFilterPanel: React.FC<ListFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Define form fields configuration
  const filterFields: FormField[] = [
    {
      name: "recipientType",
      type: "select",
      label: "Recipient Type",
      placeholder: "Select recipient type",
      defaultValue: currentFilters.recipientType || "all",
      options: [
        { value: "all", label: "All Types" },
        { value: "CONSUMER", label: "Consumer" },
        { value: "TECHNICIAN", label: "Technician" },
        { value: "BUSINESS_USER", label: "Business" },
      ],
      fullWidth: true,
    },
    {
      name: "filterType",
      type: "select",
      label: "Filter Type",
      placeholder: "Select filter type",
      defaultValue: currentFilters.filterType || "all",
      options: [
        { value: "all", label: "All Filter Types" },
        { value: "STATIC", label: "Static" },
        { value: "DYNAMIC", label: "Dynamic" },
      ],
      fullWidth: true,
    },
    {
      name: "isActive",
      type: "select",
      label: "Status",
      placeholder: "Select status",
      defaultValue: currentFilters.isActive || "all",
      options: [
        { value: "all", label: "All Status" },
        { value: "true", label: "Active" },
        { value: "false", label: "Inactive" },
      ],
      fullWidth: true,
    },
  ];

  // Create form instance with default values from current filters
  const form = FormService.createForm({
    fields: filterFields,
    defaultValues: {
      recipientType: currentFilters.recipientType || "all",
      filterType: currentFilters.filterType || "all",
      isActive: currentFilters.isActive || "all",
    },
  });

  // Handle form submission
  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<ListFilters> = {};

    // Only include non-"all" values in the filters
    if (data.recipientType && data.recipientType !== "all") {
      filtersToApply.recipientType = data.recipientType;
    }

    if (data.filterType && data.filterType !== "all") {
      filtersToApply.filterType = data.filterType;
    }

    if (data.isActive && data.isActive !== "all") {
      filtersToApply.isActive = data.isActive === "true" ? [true] : [false];
    }

    onApplyFilters(filtersToApply);
  };

  // Handle form reset
  const handleResetFilters = () => {
    form.reset({
      recipientType: "all",
      filterType: "all",
      isActive: "all",
    });
    onResetFilters();
  };

  // Custom footer with Reset and Apply buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-3 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleResetFilters}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={filterFields}
      form={form}
      onSubmit={handleApplyFilters}
      onReset={handleResetFilters}
      config={{
        layout: "horizontal", // Changed from "grid" to "horizontal"
        spacing: "medium",
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        classes: {
          fieldContainer: "flex flex-col gap-1 mb-4",
          label: "text-sm text-gray-800 font-semibold",
          input: "rounded-md border px-3 py-2 border-gray-300 w-full",
          select: "rounded-md border px-3 py-2 border-gray-300",
          textarea: "rounded-md border px-3 py-2 border-gray-300",
          error: "text-sm text-red-500 mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      }}
      className="bg-white"
      gridColumns={3} // This prop controls horizontal layout
      responsiveColumns={{
        sm: 1,
        md: 3,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
};
